import { Injectable } from "@angular/core";
import { isElectron } from 'src/app/shared/utils/platform.utils';
import { CryptoService } from "../firebase/crypto.service";
import { ElectronService } from "src/app/shared/services/electron/electron.service";
import { SopoPopupService } from "src/app/sopo-popup/service/sopo-popup.service";
import { SopoLoadingService } from "src/app/shared/services/sopo-loading/sopo-loading.service";
import { addDoc, collection, deleteDoc, docData, Firestore } from "@angular/fire/firestore";
import { map, filter, firstValueFrom, race } from "rxjs";
import { environment } from "src/environments/environment";
import { SupportedWallets } from "../state/auth/auth.state";
import { DynamicContentService } from "src/app/shared/services/dynamic-content/dynamic-content.service";

@Injectable({
  providedIn: "root"
})
export class WalletHandlerService {

  constructor(
    private cryptoService: CryptoService,
    private electronService: ElectronService,
    private sopoPopupService: SopoPopupService,
    private loadingService: SopoLoadingService,
    private firestore: Firestore,
    private dynamicContentService: DynamicContentService,
  ) { }

  public async disconnectPhantom(): Promise<void> {
    this.cryptoService.disconnectPhantom();
  }

  async signInWithWallet(wallet: SupportedWallets, userIdToLink?: string){
    if (wallet === 'abstract') {
        const collectionRef = collection(this.firestore, 'externalLoginAttempts');
        const loginOptions = {
            provider: "abstract"
        };
        if (userIdToLink) loginOptions['userIdToLink'] = userIdToLink;
        const docRef = await addDoc(collectionRef, loginOptions);
        const appUrl = environment.staging ? 'https://abstract.staging.playsopo.com/' : 'https://abstract.prod.playsopo.com/';
        const componentRef = this.dynamicContentService.openAbstractLogin(`${appUrl}?requestId=${docRef.id}`);
        const resultObs = docData(docRef).pipe(
            map(doc => doc as IWalletLoginRequest),
            filter(data => data.response !== undefined),
            map(data => data.response!)
        );
        const result = await firstValueFrom(race(componentRef.instance.getCloseObservable(), resultObs));
        this.dynamicContentService.closeAbstractLogin();

        await deleteDoc(docRef);

        if (result.success === false) {
            throw new Error(result.reason);
        }
        return result.credentialToken;
    }
    // if (this.walletData === null) await this.connect();

    // const getNonceResult = await httpsCallable<{publicKey: string}, {nonce: string}>(this.functions, 'getNonceToSign')({
    //     publicKey: this.walletData.public_key
    // });

    // const nonceToSign = getNonceResult.data.nonce;
    // const statement = "Sign this message to sign in to the app.";

    // const signature = await this.signMessage(`${statement}\nNonce: ${nonceToSign}`);

    // const verifySigbnatureResult = await httpsCallable<{publicKey: string, signature: string, statement: string}, {customToken: string}>(this.functions, 'verifySignedMessage')({
    //     publicKey: this.walletData.public_key,
    //     signature,
    //     statement
    // });

    // return verifySigbnatureResult.data.customToken;
}

  public async connectPhantom(): Promise<void> {
    this.loadingService.present('profile');
    try {
      let address = '';
      if (isElectron()) {
        address = await this.electronService.externalPhantomConnect();
      } else {
        address = await this.connectPhantomWithExtension();
      }

      if (address) this.cryptoService.connectPhantom(address, '');
      else throw new Error('Missing address.');
    } catch (error) {
      console.error('[WalletHandlerService] - connectPhantom', error);
      this.sopoPopupService.showAlert('app',{
        header: 'Phantom Connection Failed',
        message: error.message || error,
      });
    }
    this.loadingService.dismiss('profile');
  }

  public async connectPhantomWithExtension(): Promise<string> {
    if (!this.isPhantomInstalled()) {
      throw new Error('The Phantom extension is not installed.');
    }

    const provider = (window as any).phantom?.solana;

    const response = await provider.connect();

    if (response && response.publicKey) return response.publicKey.toString();
    throw new Error('Missing response or publicKey.');
  }

  private isPhantomInstalled(): boolean {
    return !!(window as any).phantom?.solana?.isPhantom;
  }
}

export interface IWalletLoginRequest {
    provider: "abstract",
    response?: {
        success: true,
        credentialToken: string
    } | {
        success: false,
        reason: string
    }
}
